import React, { useState, useEffect } from 'react';
import { useNavigate, NavLink, Link } from 'react-router-dom';
import styled from 'styled-components';
import PlanejadorCliente from './components/Planejador/PlanejadorCliente';

import './ClientePage.css';

// Define styled components outside of the component function
const LogoLink = styled(Link)`
  text-decoration: none;
  display: flex;
  align-items: center;
`;

const Logo = styled.div`
  display: flex;
  align-items: center;
  
  h1 {
    font-size: 1.8rem;
    color: var(--army-green);
    font-weight: 700;
    margin: 0;
  }
  
  span {
    color: var(--old-silver);
    font-style: italic;
    font-size: 1rem;
    margin-left: 0.5rem;
  }
`;

const ClientePage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [userName, setUserName] = useState('Cliente');
  const [registros, setRegistros] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [activeFaqItem, setActiveFaqItem] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAuthorized, setIsAuthorized] = useState(true);
  
  // Filter registros based on status and search term
  const filterRegistros = (items) => {
    return items.filter(item => {
      const matchesStatus = filterStatus === 'all' || item.status.toLowerCase() === filterStatus.toLowerCase();
      const matchesSearch = item.id.toLowerCase().includes(searchTerm.toLowerCase()) || 
                         item.type.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         item.description.toLowerCase().includes(searchTerm.toLowerCase());
      return matchesStatus && matchesSearch;
    });
  };
  
  // Reset filters
  const resetFilters = () => {
    setFilterStatus('all');
    setSearchTerm('');
  };
  
  // Auth check effect - separated to avoid issues with early returns
  useEffect(() => {
    const isAuthenticated = localStorage.getItem('isAuthenticated');
    const userRole = localStorage.getItem('userRole');
    
    if (!isAuthenticated || userRole !== 'cliente') {
      setIsAuthorized(false);
      navigate('/login');
    } else {
      setIsAuthorized(true);
    }
  }, [navigate]);
  
  // Data loading effect
  useEffect(() => {
    // Only load data if authorized
    if (!isAuthorized) return;
    
    const storedName = localStorage.getItem('userName');
    if (storedName) {
      setUserName(storedName);
    }
    
    // Mock data for registros
    const mockRegistros = [
      {
        id: 'REG-001',
        type: 'Registro de Empresa',
        status: 'Em andamento',
        date: '05/04/2025',
        statusClass: 'status-in-progress',
        description: 'Registro de abertura de empresa LTDA'
      },
      {
        id: 'REG-002',
        type: 'Alteração Contratual',
        status: 'Concluído',
        date: '10/03/2025',
        statusClass: 'status-completed',
        description: 'Alteração de endereço e atividades'
      },
      {
        id: 'SOL-003',
        type: 'Registro de Marca',
        status: 'Pendente',
        date: '12/04/2025',
        statusClass: 'status-pending',
        description: 'Solicitação de registro de marca "Empresa XYZ"'
      },
      {
        id: 'SOL-004',
        type: 'Registro de Patente',
        status: 'Recusado',
        date: '01/02/2025',
        statusClass: 'status-rejected',
        description: 'Solicitação recusada devido a documentação insuficiente'
      }
    ];
    
    // Mock data for documents
    const mockDocuments = [
      {
        id: 'DOC-001',
        title: 'Contrato Social',
        type: 'PDF',
        date: '10/03/2025',
        size: '1.2 MB'
      },
      {
        id: 'DOC-002',
        title: 'Certidão de CNPJ',
        type: 'PDF',
        date: '05/02/2025',
        size: '450 KB'
      },
      {
        id: 'DOC-003',
        title: 'Alvará de Funcionamento',
        type: 'PDF',
        date: '15/01/2025',
        size: '750 KB'
      },
      {
        id: 'DOC-004',
        title: 'Inscrição Estadual',
        type: 'PDF',
        date: '20/12/2024',
        size: '320 KB'
      }
    ];
    
    setRegistros(mockRegistros);
    setDocuments(mockDocuments);
  }, [isAuthorized, navigate]);
  
  const handleLogout = () => {
    localStorage.removeItem('isAuthenticated');
    localStorage.removeItem('userRole');
    localStorage.removeItem('userName');
    navigate('/login');
  };
  
  const toggleFaqItem = (index) => {
    if (activeFaqItem === index) {
      setActiveFaqItem(null);
    } else {
      setActiveFaqItem(index);
    }
  };
  
  const faqItems = [
    {
      question: 'Como acompanhar o status do meu registro ou solicitação?',
      answer: 'Você pode acompanhar o status do seu registro ou solicitação na seção "Registros e Solicitações" nesta página. Cada item tem um indicador de status que é atualizado em tempo real.'
    },
    {
      question: 'Quanto tempo leva para registrar uma empresa na Junta Comercial?',
      answer: 'O tempo médio para registro de uma empresa na Junta Comercial varia de 3 a 7 dias úteis, dependendo da complexidade e do volume de solicitações.'
    },
    {
      question: 'Que documentos são necessários para uma alteração contratual?',
      answer: 'Para uma alteração contratual, você precisará de: contrato social consolidado, documentos pessoais dos sócios, requerimento de empresário (se MEI), e formulário específico da alteração pretendida.'
    },
    {
      question: 'Como solicitar uma nova certidão?',
      answer: 'Para solicitar uma nova certidão, clique no botão "Nova Solicitação" e selecione a opção "Certidões" no menu dropdown. Preencha o formulário e envie sua solicitação.'
    }
  ];
  
  // Always return JSX, even if the user is not authorized
  return (
    <div className="cliente-wrapper">
      <header className="cliente-header">
        <LogoLink to="/">
          <Logo>
            <h1>Veridicus</h1>
            <span>"BackOffice Inteligente & Gestão Documental"</span>
          </Logo>
        </LogoLink>
        
        <nav className="cliente-nav">
          <NavLink 
            to="#" 
            className={`cliente-nav-link ${activeTab === 'dashboard' ? 'active' : ''}`} 
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </NavLink>
          <NavLink 
            to="#" 
            className={`cliente-nav-link ${activeTab === 'planner' ? 'active' : ''}`}
            onClick={() => setActiveTab('planner')}
          >
            Pedidos e Serviços
          </NavLink>
          <NavLink 
            to="#" 
            className={`cliente-nav-link ${activeTab === 'processes' ? 'active' : ''}`}
            onClick={() => setActiveTab('processes')}
          >
            Registros e Solicitações
          </NavLink>
          <NavLink 
            to="#" 
            className={`cliente-nav-link ${activeTab === 'documents' ? 'active' : ''}`}
            onClick={() => setActiveTab('documents')}
          >
            Documentos
          </NavLink>
          <NavLink 
            to="#" 
            className={`cliente-nav-link ${activeTab === 'faqs' ? 'active' : ''}`}
            onClick={() => setActiveTab('faqs')}
          >
            FAQs
          </NavLink>
        </nav>
        
        <div className="cliente-user-info">
          <span className="user-welcome">Olá, {userName}</span>
          <button 
            className="cliente-btn btn-danger"
            onClick={handleLogout}
          >
            Sair
          </button>
        </div>
      </header>
      
      <main className="cliente-main">
        <div className="cliente-container">
          {activeTab === 'dashboard' && (
            <>
              <div className="cliente-section-header">
                <h1>Área do Cliente</h1>
                <p>Acesse seus registros, documentos e solicitações</p>
              </div>
              
              <div className="cliente-dashboard">
                <div className="cliente-card">
                  <div className="cliente-card-header">
                    <div className="cliente-card-icon">📋</div>
                    <h3>Meus Registros</h3>
                  </div>
                  <div className="cliente-card-content">
                    <p>Acompanhe o status de todos os seus registros e solicitações na Junta Comercial.</p>
                  </div>
                  <div className="cliente-card-footer">
                    <button 
                      className="cliente-btn btn-primary"
                      onClick={() => setActiveTab('processes')}
                    >
                      Ver Registros
                    </button>
                  </div>
                </div>
                
                <div className="cliente-card">
                  <div className="cliente-card-header">
                    <div className="cliente-card-icon">📄</div>
                    <h3>Documentos</h3>
                  </div>
                  <div className="cliente-card-content">
                    <p>Acesse e faça download de todos os seus documentos importantes e certidões.</p>
                  </div>
                  <div className="cliente-card-footer">
                    <button 
                      className="cliente-btn btn-primary"
                      onClick={() => setActiveTab('documents')}
                    >
                      Ver Documentos
                    </button>
                  </div>
                </div>
                
                <div className="cliente-card">
                  <div className="cliente-card-header">
                    <div className="cliente-card-icon">📋</div>
                    <h3>Pedidos e Serviços</h3>
                  </div>
                  <div className="cliente-card-content">
                    <p>Acompanhe o progresso dos seus pedidos e serviços com informações financeiras.</p>
                  </div>
                  <div className="cliente-card-footer">
                    <button 
                      className="cliente-btn btn-primary"
                      onClick={() => setActiveTab('planner')}
                    >
                      Ver Pedidos
                    </button>
                  </div>
                </div>
                
                <div className="cliente-card">
                  <div className="cliente-card-header">
                    <div className="cliente-card-icon">📝</div>
                    <h3>Nova Solicitação</h3>
                  </div>
                  <div className="cliente-card-content">
                    <p>Inicie um novo registro, alteração, baixa ou solicite certidões e documentos.</p>
                  </div>
                  <div className="cliente-card-footer">
                    <button 
                      className="cliente-btn btn-primary"
                      onClick={() => navigate('/cliente/nova-solicitacao')}
                    >
                      Solicitar
                    </button>
                  </div>
                </div>
              </div>
              
              <div className="cliente-registros">
                <div className="cliente-registros-header">
                  <h2>Registros e Solicitações Recentes</h2>
                  <div className="header-actions">
                    <div className="filtro-busca-compact">
                      <input
                        type="text"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="filtro-input"
                      />
                    </div>
                    <button 
                      className="cliente-btn btn-text"
                      onClick={() => setActiveTab('processes')}
                    >
                      Ver Todos
                    </button>
                  </div>
                </div>
                
                {registros.length > 0 ? (
                  filterRegistros(registros).length > 0 ? (
                    <table className="cliente-registro-table">
                      <thead>
                        <tr>
                          <th>Protocolo</th>
                          <th>Tipo</th>
                          <th>Status</th>
                          <th>Data</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterRegistros(registros).slice(0, 3).map((registro, index) => (
                          <tr key={index}>
                            <td>{registro.id}</td>
                            <td>{registro.type}</td>
                            <td>
                              <span className={`status-badge ${registro.statusClass}`}>
                                {registro.status}
                              </span>
                            </td>
                            <td>{registro.date}</td>
                            <td>
                              <button 
                                className="cliente-btn btn-text"
                                onClick={() => navigate(`/cliente/registro/${registro.id}`)}
                              >
                                Detalhes
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-state-icon">🔍</div>
                      <h3 className="empty-state-title">Nenhum resultado encontrado</h3>
                      <p className="empty-state-text">
                        Não foram encontrados registros que correspondam à sua busca.
                      </p>
                      <button 
                        className="cliente-btn btn-secondary"
                        onClick={resetFilters}
                      >
                        Limpar busca
                      </button>
                    </div>
                  )
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">📋</div>
                    <h3 className="empty-state-title">Nenhum registro encontrado</h3>
                    <p className="empty-state-text">
                      Você ainda não possui registros ou solicitações. Clique em "Nova Solicitação" para começar.
                    </p>
                    <button 
                      className="cliente-btn btn-primary"
                      onClick={() => navigate('/cliente/nova-solicitacao')}
                    >
                      Nova Solicitação
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          
          {activeTab === 'processes' && (
            <>
              <div className="cliente-registros">
                <div className="cliente-registros-header">
                  <h2>Meus Registros e Solicitações</h2>
                  <button 
                    className="cliente-btn btn-primary"
                    onClick={() => navigate('/cliente/nova-solicitacao')}
                  >
                    Nova Solicitação
                  </button>
                </div>
                
                <div className="cliente-filtros">
                  <div className="filtro-busca">
                    <input
                      type="text"
                      placeholder="Buscar por protocolo, tipo ou descrição..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="filtro-input"
                    />
                  </div>
                  
                  <div className="filtro-status">
                    <select 
                      value={filterStatus} 
                      onChange={(e) => setFilterStatus(e.target.value)}
                      className="filtro-select"
                    >
                      <option value="all">Todos os status</option>
                      <option value="em andamento">Em andamento</option>
                      <option value="pendente">Pendente</option>
                      <option value="concluído">Concluído</option>
                      <option value="recusado">Recusado</option>
                    </select>
                  </div>
                  
                  <button 
                    className="cliente-btn btn-text"
                    onClick={resetFilters}
                  >
                    Limpar filtros
                  </button>
                </div>
                
                {registros.length > 0 ? (
                  filterRegistros(registros).length > 0 ? (
                    <table className="cliente-registro-table">
                      <thead>
                        <tr>
                          <th>Protocolo</th>
                          <th>Tipo</th>
                          <th>Status</th>
                          <th>Data</th>
                          <th>Descrição</th>
                          <th>Ações</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filterRegistros(registros).map((registro, index) => (
                          <tr key={index}>
                            <td>{registro.id}</td>
                            <td>{registro.type}</td>
                            <td>
                              <span className={`status-badge ${registro.statusClass}`}>
                                {registro.status}
                              </span>
                            </td>
                            <td>{registro.date}</td>
                            <td>{registro.description}</td>
                            <td>
                              <button 
                                className="cliente-btn btn-text"
                                onClick={() => navigate(`/cliente/registro/${registro.id}`)}
                              >
                                Detalhes
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className="empty-state">
                      <div className="empty-state-icon">🔍</div>
                      <h3 className="empty-state-title">Nenhum resultado encontrado</h3>
                      <p className="empty-state-text">
                        Não foram encontrados registros que correspondam aos filtros selecionados.
                      </p>
                      <button 
                        className="cliente-btn btn-secondary"
                        onClick={resetFilters}
                      >
                        Limpar filtros
                      </button>
                    </div>
                  )
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">📋</div>
                    <h3 className="empty-state-title">Nenhum registro encontrado</h3>
                    <p className="empty-state-text">
                      Você ainda não possui registros ou solicitações. Clique em "Nova Solicitação" para começar.
                    </p>
                    <button 
                      className="cliente-btn btn-primary"
                      onClick={() => navigate('/cliente/nova-solicitacao')}
                    >
                      Nova Solicitação
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          
          {activeTab === 'documents' && (
            <>
              <div className="cliente-registros">
                <div className="cliente-registros-header">
                  <h2>Meus Documentos</h2>
                  <button 
                    className="cliente-btn btn-primary"
                    onClick={() => navigate('/cliente/novo-documento')}
                  >
                    Enviar Documento
                  </button>
                </div>
                
                {documents.length > 0 ? (
                  <div className="documento-grid">
                    {documents.map((doc, index) => (
                      <div className="documento-card" key={index}>
                        <div className="documento-icon">📄</div>
                        <h3 className="documento-title">{doc.title}</h3>
                        <div className="documento-date">
                          <span>Atualizado: {doc.date}</span>
                          <span> • {doc.size}</span>
                        </div>
                        <button className="cliente-btn btn-secondary">
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-state">
                    <div className="empty-state-icon">📄</div>
                    <h3 className="empty-state-title">Nenhum documento encontrado</h3>
                    <p className="empty-state-text">
                      Você ainda não possui documentos. Envie um documento ou crie uma nova solicitação.
                    </p>
                    <button 
                      className="cliente-btn btn-primary"
                      onClick={() => navigate('/cliente/novo-documento')}
                    >
                      Enviar Documento
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
          
          {activeTab === 'faqs' && (
            <div className="cliente-faq">
              <div className="cliente-faq-header">
                <h2>Perguntas Frequentes</h2>
              </div>
              
              {faqItems.map((item, index) => (
                <div className="faq-item" key={index}>
                  <div 
                    className={`faq-question ${activeFaqItem === index ? 'active' : ''}`}
                    onClick={() => toggleFaqItem(index)}
                  >
                    <span>{item.question}</span>
                    <span className="faq-toggle">+</span>
                  </div>
                  {activeFaqItem === index && (
                    <div className="faq-answer">
                      {item.answer}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          
          {activeTab === 'planner' && <PlanejadorCliente />}
        </div>
      </main>
    </div>
  );
};

export default ClientePage;